/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef, type PointerEvent } from "react";
import { mergeProps, useFocusRing, useHover, useLink } from "react-aria";

import { useIsMobile, useTypo, useUiColors } from "@superweb/ui";
import { cssFns } from "@superweb/css";

type Icon =
  | "rental-car-taxi-profession"
  | "personal-car-taxi-profession"
  | "intercity-driver-profession"
  | "walking-courier-profession"
  | "bicycle-courier-profession"
  | "scooter-courier-profession"
  | "motorcycle-courier-profession"
  | "truck-courier-profession"
  | "shift-worker-profession"
  | "car-courier-profession"
  | "park-employee"
  | "selfemployed"
  | "individual-entrepreneur";

export const LinkPanel = ({
  onPress,
  icon,
  label,
  description,
  subLabel,
  link,
}: {
  onPress: () => void;
  icon: Icon;
  label: string;
  subLabel?: string;
  description: string;
  link?: {
    href: string;
    onClick?: (event: PointerEvent<HTMLAnchorElement>) => void;
    external?: boolean;
  };
}) => {
  const typo = useTypo();
  const isMobile = useIsMobile();
  const uiColors = useUiColors();

  const ref = useRef<HTMLAnchorElement>(null);
  const { linkProps } = useLink(
    {
      "aria-label": label,
      onPress,
      href: link?.href,
      ...(link?.external && { target: "_blank", rel: "noreferrer" }),
    },
    ref,
  );
  const { hoverProps, isHovered } = useHover({});
  const { focusProps, isFocusVisible: isFocused } = useFocusRing({});

  return (
    <a
      {...mergeProps(hoverProps, focusProps, linkProps)}
      ref={ref}
      css={{
        ...cssFns.margin("0"),
        ...cssFns.padding("0"),
        color: uiColors.text,
        outlineStyle: "none",
        backgroundColor: "transparent",
        textDecorationLine: "none",
        ...(isMobile
          ? {
              width: "160px",
              minHeight: "200px",
              flexDirection: "column",
              alignItems: "center",
              gridTemplateRows: "1fr auto",
            }
          : {
              width: "100%",
              minHeight: "234px",
              gridTemplateColumns: "auto 1fr",
            }),
        cursor: "pointer",
        display: "grid",
        textAlign: "start",
        boxSizing: "border-box",
        boxShadow:
          isHovered || isFocused
            ? "inset 0 0 0 2px " + uiColors.focus
            : "inset 0 0 0 1px " + uiColors.line,
        ...cssFns.border({
          style: "none",
          radius: "20px",
        }),
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBlockStart: isMobile ? "24px" : "0",
        }}
      >
        <img
          src={new URL(`../images/${icon}.svg`, import.meta.url).href}
          css={{
            objectFit: "contain",
            maxWidth: isMobile ? "112px" : "248px",
            width: "100%",
          }}
          alt=""
        />
      </div>
      <div
        css={{
          flexGrow: "1",
          rowGap: "16px",
          display: "flex",
          whiteSpace: "pre-wrap",
          flexDirection: "column",
          justifyContent: "space-between",
          paddingBlockStart: isMobile ? "18px" : "20px",
          paddingBlockEnd: isMobile ? "12px" : "16px",
          paddingInlineStart: isMobile ? "16px" : "8px",
          paddingInlineEnd: "16px",

          ...cssFns.overflow("hidden"),
          ...typo({
            level: isMobile ? "caption1" : "body1",
            density: isMobile ? "tight" : "normal",
            weight: isMobile ? "medium" : "regular",
          }),

          ...(isMobile && {
            textAlign: "center",
          }),
        }}
      >
        <div>
          <div>{label}</div>
          {!isMobile && subLabel ? (
            <div
              css={{
                ...typo({
                  level: "body2",
                  density: "normal",
                  weight: "regular",
                }),
                color: uiColors.textMinor,
              }}
            >
              {subLabel}
            </div>
          ) : null}
        </div>
        {!isMobile && <div>{description}</div>}
      </div>
    </a>
  );
};
